/**
 * EMZI NEXUS V6.3 - Content Studio V2 - Test Harness
 * Minimal UI to validate backend endpoints before building full 9-step wizard
 */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../api/contentStudioV2';

const ContentStudioV2TestHarness = () => {
  const { user, token } = useAuth();
  const [sessionId] = useState(`TEST_${Date.now()}`);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState({});

  // Form states
  const [objective, setObjective] = useState('Sales');
  const [productPacks, setProductPacks] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [soaSessionId, setSoaSessionId] = useState('');
  const [submissionId, setSubmissionId] = useState('');

  const handleError = (err) => {
    setError(err.message || 'An error occurred');
    setLoading(false);
  };

  const updateResult = (stepName, data) => {
    setResults(prev => ({ ...prev, [stepName]: data }));
  };

  // Panel 1: Context Init (Steps 1-4)
  const testContextInit = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Step 1: Objective
      const objRes = await api.setObjective(token, sessionId, user.user_id, objective);
      updateResult('objective', objRes);
      
      // Step 2: Audience
      const audRes = await api.setTargetedAudience(token, sessionId, {
        country: 'Malaysia',
        state: 'Selangor',
        age_range: '25-45',
        gender: 'All',
        location_type: 'Urban',
        interests: ['Health', 'Fitness'],
        primary_language: 'Mix',
        slang_style: 'Santai',
        audience_narrative: 'Mak-mak 40an risau kesihatan'
      });
      updateResult('audience', audRes);
      
      // Get product packs for selection
      const packsRes = await api.getKnowledgePacksByType(token, 'Product');
      setProductPacks(packsRes.packs || []);
      
      if (packsRes.packs && packsRes.packs.length > 0) {
        setSelectedProduct(packsRes.packs[0].pack_id);
        
        // Step 3: Knowledge Selection
        const knowRes = await api.selectKnowledge(token, sessionId, objective, {
          products: [packsRes.packs[0].pack_id],
          services: [],
          values: []
        });
        updateResult('knowledge', knowRes);
        
        // Step 4: Evidence Mode
        const evRes = await api.setEvidenceMode(token, sessionId, 'Hybrid');
        updateResult('evidence', evRes);
        
        setStep(2);
        setLoading(false);
        alert('✅ Context Init complete! Proceed to SOA Panel.');
      }
    } catch (err) {
      handleError(err);
    }
  };

  // Panel 2: SOA Drill (Step 5)
  const testSOADrill = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Start SOA Drill
      const startRes = await api.startSOADrill(token, sessionId, objective, {});
      updateResult('soa_start', startRes);
      setSoaSessionId(startRes.soa_session_id);
      
      // Answer Question 1
      const ansRes1 = await api.answerSOAQuestion(
        token,
        sessionId,
        startRes.soa_session_id,
        startRes.current_question.question_id,
        'Masalah utama audiens adalah tekanan darah tinggi yang menganggu kehidupan harian dan menyebabkan risau.',
        1
      );
      updateResult('soa_answer1', ansRes1);
      
      // Answer remaining 4 questions (simplified for test harness)
      let currentQuestion = ansRes1.next_question;
      for (let i = 2; i <= 5 && currentQuestion; i++) {
        const ansRes = await api.answerSOAQuestion(
          token,
          sessionId,
          startRes.soa_session_id,
          currentQuestion.question_id,
          `Test answer for question ${i} with sufficient detail to pass scoring.`,
          1
        );
        updateResult(`soa_answer${i}`, ansRes);
        currentQuestion = ansRes.next_question;
      }
      
      // Finalize SOA
      const finalRes = await api.finalizeSOADrill(token, sessionId, startRes.soa_session_id, false);
      updateResult('soa_finalize', finalRes);
      
      setStep(3);
      setLoading(false);
      alert(`✅ SOA Drill complete! Average: ${finalRes.average_score}, BP: +${finalRes.bp_earned}`);
    } catch (err) {
      handleError(err);
    }
  };

  // Panel 3: Script + Platform + Format (Steps 6-8)
  const testScriptPlatformFormat = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Generate Scripts
      const scriptRes = await api.generateScripts(token, sessionId, soaSessionId);
      updateResult('scripts', scriptRes);
      
      // Select Script A
      const selectRes = await api.selectScript(token, sessionId, 'A', 'Selected variant A');
      updateResult('script_select', selectRes);
      
      // Set Platform & Duration
      const platRes = await api.setPlatformDuration(token, sessionId, ['TikTok', 'Instagram'], '1m 25s');
      updateResult('platform', platRes);
      
      // Suggest Formats
      const fmtRes = await api.suggestFormats(token, sessionId);
      updateResult('formats', fmtRes);
      
      // Select Format
      const fmtSelRes = await api.selectFormat(token, sessionId, fmtRes.format_options[0].format_id);
      updateResult('format_select', fmtSelRes);
      
      setStep(4);
      setLoading(false);
      alert('✅ Script/Platform/Format complete! Proceed to Approval.');
    } catch (err) {
      handleError(err);
    }
  };

  // Panel 4: Approval & Blueprint (Step 9)
  const testApprovalBlueprint = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Submit for Approval
      const subRes = await api.submitForApproval(token, sessionId, 'Test submission');
      updateResult('submission', subRes);
      setSubmissionId(subRes.submission_id);
      
      // Supervisor Approve (simulate)
      const appRes = await api.supervisorDecide(
        token,
        sessionId,
        subRes.submission_id,
        'Approved',
        'Looks good!'
      );
      updateResult('approval', appRes);
      
      // Generate Production Blueprint
      const bpRes = await api.generateProductionBlueprint(token, sessionId, subRes.submission_id);
      updateResult('blueprint', bpRes);
      
      setStep(5);
      setLoading(false);
      alert('✅ Full flow complete! Production Blueprint generated.');
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>🧪 Content Studio V2 - Test Harness</h1>
      <p><strong>Session ID:</strong> {sessionId}</p>
      <p><strong>User:</strong> {user?.email} ({user?.role})</p>
      
      {error && (
        <div style={{ background: '#fee', padding: '10px', marginBottom: '20px', border: '1px solid #c00' }}>
          <strong>Error:</strong> {error}
        </div>
      )}
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Panel 1: Context Init */}
        <div style={{ border: '1px solid #ccc', padding: '15px', background: step >= 1 ? '#e8f5e9' : '#fff' }}>
          <h3>1️⃣ Context Init (Steps 1-4)</h3>
          <div>
            <label>Objective:</label>
            <select value={objective} onChange={(e) => setObjective(e.target.value)} disabled={step > 1}>
              <option value="Sales">Sales</option>
              <option value="Branding">Branding</option>
              <option value="Awareness">Awareness</option>
            </select>
          </div>
          <button 
            onClick={testContextInit} 
            disabled={loading || step > 1}
            style={{ marginTop: '10px', padding: '10px 20px', background: '#0e67a2', color: '#fff', border: 'none', cursor: 'pointer' }}
          >
            {loading && step === 1 ? 'Testing...' : step > 1 ? '✅ Complete' : 'Test Context Init'}
          </button>
          {results.knowledge && (
            <div style={{ marginTop: '10px', fontSize: '12px' }}>
              <strong>Knowledge Spider:</strong>
              <pre style={{ background: '#f5f5f5', padding: '5px', fontSize: '11px', maxHeight: '150px', overflow: 'auto' }}>
                {JSON.stringify(results.knowledge.knowledge_spider, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Panel 2: SOA Drill */}
        <div style={{ border: '1px solid #ccc', padding: '15px', background: step >= 2 && step < 3 ? '#fff3e0' : step >= 3 ? '#e8f5e9' : '#f5f5f5' }}>
          <h3>2️⃣ SOA Drill (Step 5)</h3>
          <p>5 Questions, 3 Loops, Adaptive</p>
          <button 
            onClick={testSOADrill} 
            disabled={loading || step !== 2}
            style={{ marginTop: '10px', padding: '10px 20px', background: step === 2 ? '#0e67a2' : '#ccc', color: '#fff', border: 'none', cursor: step === 2 ? 'pointer' : 'not-allowed' }}
          >
            {loading && step === 2 ? 'Testing...' : step > 2 ? '✅ Complete' : 'Test SOA Drill'}
          </button>
          {results.soa_finalize && (
            <div style={{ marginTop: '10px', fontSize: '12px' }}>
              <strong>Results:</strong><br />
              Avg Score: {results.soa_finalize.average_score}<br />
              BP Earned: +{results.soa_finalize.bp_earned}<br />
              Tier: {results.soa_finalize.performance_tier}<br />
              Flagged: {results.soa_finalize.supervisor_flagged ? 'Yes' : 'No'}
            </div>
          )}
        </div>

        {/* Panel 3: Script/Platform/Format */}
        <div style={{ border: '1px solid #ccc', padding: '15px', background: step >= 3 && step < 4 ? '#fff3e0' : step >= 4 ? '#e8f5e9' : '#f5f5f5' }}>
          <h3>3️⃣ Script/Platform/Format (Steps 6-8)</h3>
          <p>A/B/C Scripts, Duration, Viral Potential</p>
          <button 
            onClick={testScriptPlatformFormat} 
            disabled={loading || step !== 3}
            style={{ marginTop: '10px', padding: '10px 20px', background: step === 3 ? '#0e67a2' : '#ccc', color: '#fff', border: 'none', cursor: step === 3 ? 'pointer' : 'not-allowed' }}
          >
            {loading && step === 3 ? 'Testing...' : step > 3 ? '✅ Complete' : 'Test Script/Platform/Format'}
          </button>
          {results.format_select && (
            <div style={{ marginTop: '10px', fontSize: '12px' }}>
              <strong>Selected Format:</strong><br />
              {results.format_select.selected_format.format_name}<br />
              Viral: {results.format_select.selected_format.viral_potential}%
            </div>
          )}
        </div>

        {/* Panel 4: Approval & Blueprint */}
        <div style={{ border: '1px solid #ccc', padding: '15px', background: step >= 4 && step < 5 ? '#fff3e0' : step >= 5 ? '#e8f5e9' : '#f5f5f5' }}>
          <h3>4️⃣ Approval & Blueprint (Step 9)</h3>
          <p>Supervisor Approval, Production Blueprint</p>
          <button 
            onClick={testApprovalBlueprint} 
            disabled={loading || step !== 4}
            style={{ marginTop: '10px', padding: '10px 20px', background: step === 4 ? '#0e67a2' : '#ccc', color: '#fff', border: 'none', cursor: step === 4 ? 'pointer' : 'not-allowed' }}
          >
            {loading && step === 4 ? 'Testing...' : step > 4 ? '✅ Complete' : 'Test Approval & Blueprint'}
          </button>
          {results.blueprint && (
            <div style={{ marginTop: '10px', fontSize: '12px' }}>
              <strong>Blueprint ID:</strong> {results.blueprint.blueprint.blueprint_id}<br />
              <strong>Status:</strong> {results.blueprint.blueprint.status}
            </div>
          )}
        </div>
      </div>

      {/* Full Results JSON */}
      <div style={{ marginTop: '30px' }}>
        <h3>📋 Full Results (JSON)</h3>
        <pre style={{ background: '#f5f5f5', padding: '15px', fontSize: '11px', maxHeight: '400px', overflow: 'auto' }}>
          {JSON.stringify(results, null, 2)}
        </pre>
      </div>
    </div>
  );
};

export default ContentStudioV2TestHarness;
